import React, { lazy } from 'react';

import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import SummarizeIcon from '@mui/icons-material/SummarizeOutlined';
import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';

import { BLANK_PAGE_ROUTE } from '@routes';
import {
  APPROVE_ITEM_BANK_QUESTIONS,
  DELEGATE_MANAGE_BENCHMARK_TEST_BANK,
  EDIT_ITEM_BANK_QUESTIONS,
  MANAGE_CURRICULUM_DELEGATES,
  MANAGE_ITEM_BANK,
  MANAGE_CUSTOM_ITEM_BANK,
  CAN_APPROVE_PENDING_QUESTIONS
} from '@rights';
import Breadcrumb from '@common/components/breadcrumb/breadcrumb';

import { ItemBankBundle, CustomItemBanksBundle } from '@aware/bundles';
import V2SettingsIframe from '@aware/common/v2-settings-iframe';
import { V2_ROUTE_AREAS, V2_ROUTE_PAGES } from '@aware/v2-routes/constants';
import CreateNewDelegate from '@aware/common/v2-settings/create-new-delegate';
import V2ReportsPage from '@aware/common/v2-reports-page/v2-reports-page';

const V2ItemBankReportsPage = () => (
  <V2ReportsPage tabId="ItemBank" baseRoute={V2_ROUTE_PAGES.itemBankReports} />
);

const Blank = () => null;

const ITEM_BANK_ROUTE_CONFIG = {
  title: 'Item Bank',
  breadcrumb: {
    path: V2_ROUTE_AREAS.itemBank,
    title: 'Item Bank'
  },
  settings: [
    {
      title: 'Local Item Bank',
      order: 1,
      paths: [
        {
          path: `${V2_ROUTE_PAGES.itemBankSettings}/workflows`,
          title: 'Item Bank Workflows',
          rights: [{ right: [MANAGE_ITEM_BANK] }],
          exact: true,
          component: () => (
            <V2SettingsIframe
              iframeSrc={`/aware/?embed=1&leftSrc=/aware/manage/workflow/workflowlist.aspx&detailsSrc=${BLANK_PAGE_ROUTE}&rightSrc=${BLANK_PAGE_ROUTE}`}
              title="Item Bank Workflows"
            />
          )
        },
        {
          path: `${V2_ROUTE_PAGES.itemBankSettings}/new-workflow`,
          title: 'New Item Bank Workflow',
          rights: [{ right: [MANAGE_ITEM_BANK] }],
          exact: true,
          component: () => (
            <V2SettingsIframe
              iframeSrc={`/aware/?embed=1&leftSrc=/aware/manage/workflow/workflowlist.aspx&detailsSrc=${BLANK_PAGE_ROUTE}&rightSrc=${BLANK_PAGE_ROUTE}`}
              onLoadCallback={
                () => window.top.panelleft1.__doPostBack('tbbAddWorkflow', '') // eslint-disable-line no-underscore-dangle
              }
              title="New Item Bank Workflow"
            />
          )
        },
        {
          path: `${V2_ROUTE_PAGES.itemBankSettings}/assign-delegates`,
          title: 'Assign Delegates',
          rights: [{ right: [MANAGE_CURRICULUM_DELEGATES] }],
          exact: true,
          component: () => (
            <V2SettingsIframe
              iframeSrc={`/aware/?embed=1&leftSrc=/webmanager/curriculum/delegatelist2.aspx&detailsSrc=${BLANK_PAGE_ROUTE}&rightSrc=${BLANK_PAGE_ROUTE}`}
              title="Assign Delegates"
            />
          )
        },
        {
          path: `${V2_ROUTE_PAGES.itemBankSettings}/create-new-delegate`,
          title: 'Create New Delegate',
          rights: [{ right: [MANAGE_CURRICULUM_DELEGATES] }],
          exact: true,
          component: CreateNewDelegate
        },
        {
          path: `${V2_ROUTE_PAGES.itemBankSettings}/approve-pending-questions`,
          title: 'Approve Pending Questions',
          rights: [
            {
              right: [
                APPROVE_ITEM_BANK_QUESTIONS,
                MANAGE_ITEM_BANK,
                EDIT_ITEM_BANK_QUESTIONS,
                DELEGATE_MANAGE_BENCHMARK_TEST_BANK,
                CAN_APPROVE_PENDING_QUESTIONS // Item Bank Workflows: User is assigned to approve questions
              ]
            }
          ],
          exact: true,
          component: () => <ItemBankBundle approvePendingQuestions />
        },
        {
          path: `${V2_ROUTE_PAGES.itemBankSettings}/set-access-method`,
          title: 'Set Access Method',
          rights: [{ right: [MANAGE_ITEM_BANK, EDIT_ITEM_BANK_QUESTIONS] }],
          exact: true,
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&detailsSrc=/aware/benchmarkbankmode.aspx?embed=1"
              title="Set Access Method"
            />
          )
        },
        {
          path: `${V2_ROUTE_PAGES.itemBankSettings}/set-default-security`,
          title: 'Set Default Security',
          rights: [{ right: [MANAGE_ITEM_BANK, EDIT_ITEM_BANK_QUESTIONS] }],
          exact: true,
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&detailsSrc=/aware/manage/benchmark/benchmarkbankdefaultsecurity.aspx"
              title="Set Default Security"
            />
          )
        }
      ]
    },
    {
      title: 'Custom Item Banks',
      order: 2,
      paths: [
        {
          path: `${V2_ROUTE_AREAS.itemBank}/custom`,
          title: 'Manage Custom Banks',
          rights: [{ right: [MANAGE_CUSTOM_ITEM_BANK] }],
          component: CustomItemBanksBundle,
          featureFlagCheck: configuration => configuration.awareCustomBankMain === 'True',
          breadcrumb: () => (
            <Breadcrumb title="Custom Banks" to={`${V2_ROUTE_AREAS.itemBank}/custom`} />
          ),
          routes: [
            {
              path: `${V2_ROUTE_AREAS.itemBank}/custom/:id`,
              component: Blank,
              breadcrumb: () => <CustomItemBanksBundle renderBreadcrumb />
            },
            {
              path: `${V2_ROUTE_AREAS.itemBank}/custom/:id/imports`,
              component: Blank,
              breadcrumb: () => <Breadcrumb title="Imports" autoCalcPath />
            }
          ]
        }
      ]
    }
  ],
  tests: {
    path: V2_ROUTE_PAGES.itemBankPrebuiltTests,
    icon: <BallotOutlinedIcon />,
    title: 'Prebuilt Tests',
    breadcrumb: () => (
      <Breadcrumb title="Prebuilt Tests" to={V2_ROUTE_PAGES.itemBankPrebuiltTests} />
    ),
    order: 1,
    routes: [
      {
        path: V2_ROUTE_PAGES.itemBankPrebuiltTests,
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "aware-item-bank-tests" */ '@aware/pages/v2-prebuilt-tests/prebuilt-tests'
            )
        )
      }
    ]
  },
  questions: {
    path: V2_ROUTE_PAGES.itemBankQuestionsAndResources,
    icon: <MenuBookIcon />,
    title: 'Questions & Resources',
    breadcrumb: () => (
      <Breadcrumb
        title="Questions & Resources"
        to={V2_ROUTE_PAGES.itemBankQuestionsAndResources}
      />
    ),
    order: 2,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.itemBankQuestionsAndResources}/:courseId?`,
        component: ItemBankBundle
      }
    ]
  },
  reports: {
    path: V2_ROUTE_PAGES.itemBankReports,
    icon: <SummarizeIcon />,
    title: 'Reports',
    breadcrumb: () => <Breadcrumb title="Reports" to={V2_ROUTE_PAGES.itemBankReports} />,
    collapsedTitle: 'Item Bank Reports',
    exact: true,
    order: 3,
    component: V2ItemBankReportsPage,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.itemBankReports}/:reportId`,
        component: V2ItemBankReportsPage
      }
    ]
  }
};

export default ITEM_BANK_ROUTE_CONFIG;
